import type { ReactNode } from 'react'

import { CompanySpine } from '@/shared/components/common'
import { cn } from '@/shared/lib/cn'

export interface BarraItem {
  id: string
  label: string
  valor: number
  /**
   * La parte de `valor` que está en alerta (vencida). Se dibuja en rojo dentro
   * de la misma barra, no como una barra aparte: el largo total sigue siendo la
   * carga, y el rojo dice cuánta de esa carga ya se pasó de fecha. Dos barras
   * separadas obligarían a sumarlas mentalmente para saber cuánto tiene encima
   * esa persona, que es la pregunta original.
   */
  alerta?: number
  /** Lo que se escribe al final de la barra. Si falta, va `valor`. */
  texto?: string
  /** Hue de empresa: dibuja la espina a la izquierda. */
  hue?: number
  /** Renglón chico debajo del label (el n de la muestra, por ejemplo). */
  detalle?: string
  /** Avatar o cualquier nodo antes del nombre. */
  prefijo?: ReactNode
}

export interface BarListProps {
  items: readonly BarraItem[]
  /** Descripción de lo que muestra, para lectores de pantalla. */
  resumenAccesible: string
  /** Con `alerta` en juego hay dos colores, y dos colores piden leyenda. */
  leyenda?: { serie: string; alerta: string }
  className?: string
}

/**
 * Barras horizontales para comparar magnitudes entre pocas filas.
 *
 * Horizontal y no vertical porque las etiquetas son nombres de empresa: en
 * columnas habría que rotarlos, y un nombre rotado se lee peor que un número
 * mal alineado.
 *
 * **Todas las barras van del mismo color.** El hue de cada empresa ya está en
 * la espina de la izquierda, que es donde vive la identidad en esta app;
 * pintar además la barra con ese hue gastaría el canal del color en repetir
 * algo que ya está dicho, y dejaría el largo —que es el dato— compitiendo con
 * cuatro colores distintos.
 */
export function BarList({ items, resumenAccesible, leyenda, className }: BarListProps) {
  const tope = Math.max(1, ...items.map((i) => i.valor))

  return (
    <div className={cn('flex flex-col gap-2', className)}>
      {leyenda ? (
        <div className="flex items-center gap-3 text-2xs text-text-2">
          <span className="inline-flex items-center gap-1.5">
            <span
              aria-hidden="true"
              className="inline-block h-2 w-2 rounded-[1px]"
              style={{ backgroundColor: 'hsl(var(--chart-serie))' }}
            />
            {leyenda.serie}
          </span>
          <span className="inline-flex items-center gap-1.5">
            <span
              aria-hidden="true"
              className="inline-block h-2 w-2 rounded-[1px]"
              style={{ backgroundColor: 'hsl(var(--overdue))' }}
            />
            {leyenda.alerta}
          </span>
        </div>
      ) : null}

      <ul className="flex flex-col gap-1.5" aria-label={resumenAccesible}>
        {items.map((item) => (
          <li key={item.id} className="flex items-center gap-2">
            <div className="flex min-w-0 flex-1 items-center gap-2">
              {item.hue !== undefined ? (
                <CompanySpine hue={item.hue} label={item.label} className="h-4" />
              ) : null}
              {item.prefijo}
              <span className="min-w-0 flex-1 truncate text-xs text-text-1">{item.label}</span>
              {item.detalle ? (
                <span className="num shrink-0 text-2xs text-text-3">{item.detalle}</span>
              ) : null}
            </div>

            {/* El riel ocupa la mitad del ancho: sin un largo común, comparar dos
              barras exige medirlas contra sí mismas. */}
            <div className="flex w-1/2 items-center gap-2">
              <div className="flex h-2.5 flex-1 gap-[2px] rounded-[2px] bg-surface-2">
                {/*
                La porción en alerta va PRIMERA, apoyada en el origen: todas las
                barras arrancan en el mismo punto, así que lo rojo de dos filas
                se compara entre sí sin tener que buscar dónde empieza cada uno.
                El hueco de 2px entre los dos tramos es lo que los separa — no un
                borde, que sumaría tinta que no es dato.
              */}
                {item.alerta ? (
                  <div
                    className="h-full rounded-l-[2px]"
                    style={{
                      width: `${(Math.min(item.alerta, item.valor) / tope) * 100}%`,
                      backgroundColor: 'hsl(var(--overdue))',
                    }}
                  />
                ) : null}
                <div
                  className="h-full rounded-r-[2px]"
                  style={{
                    width: `${Math.max(2, ((item.valor - Math.min(item.alerta ?? 0, item.valor)) / tope) * 100)}%`,
                    backgroundColor: 'hsl(var(--chart-serie))',
                  }}
                />
              </div>
              <span className="num w-12 shrink-0 text-right text-2xs text-text-1">
                {item.texto ?? item.valor}
              </span>
            </div>
          </li>
        ))}
      </ul>
    </div>
  )
}
